import { selMessage } from "@selector/shared";

import type { ICreateProjectParams } from "@/api/WebApiClient.ts";
import { createErrorHandler } from "@/app-utils/errors.ts";
import type { InputModifyProjectInput, Project, ProjectList, User } from "@/graphql.ts";
import { checkCapability } from "@/modules/users/utils/user.js";
import {
  CREATE_PROJECT_FAILURE,
  CREATE_PROJECT_REQUEST,
  CREATE_PROJECT_SUCCESS,
  LOAD_PROJECTS_FAILURE,
  LOAD_PROJECTS_REQUEST,
  LOAD_PROJECTS_SUCCESS,
  MODIFY_PROJECT_REQUEST,
  MODIFY_PROJECT_SUCCESS,
} from "@/redux/action-types.ts";
import { CAPABILITY } from "@/settings/settings.ts";

import { buildCreateProjectMessage, buildModifyProjectMessage } from "../../app-utils/alert.js";
import { notify } from "../../modules/notifications/notify.js";
import { createReducer, type ReduxAction } from "../redux-utils.js";
import { getProjectLabel } from "./config.js";
import { loadProject } from "./project.js";

/**
 * State definition
 */
export interface IProjectListState {
  projects: Project[];
  isLoading: boolean;
}

export const initialState: IProjectListState = {
  projects: [],
  isLoading: false,
};

/**
 * Reducers
 */
export default createReducer(initialState, {
  [LOAD_PROJECTS_REQUEST]: (state: IProjectListState) => {
    return { ...state, isLoading: true };
  },
  [LOAD_PROJECTS_SUCCESS]: (state: IProjectListState, payload: ProjectList) => {
    return {
      ...state,
      projects: payload.projects,
      isLoading: false,
    };
  },
  [LOAD_PROJECTS_FAILURE]: (state: IProjectListState) => {
    return { ...state, isLoading: false };
  },
});

/**
 * Action creators
 */
export function loadProjectList(
  throwError = false,
  sampleTypeId: number = undefined,
  includeStagedProjects = false,
): ReduxAction<Promise<ProjectList>> {
  return (dispatch, _getState, webApiClient) => {
    dispatch({ type: LOAD_PROJECTS_REQUEST });

    return webApiClient
      .fetchProjectList(sampleTypeId, includeStagedProjects)
      .then((payload) => {
        dispatch({
          type: LOAD_PROJECTS_SUCCESS,
          payload,
        });
        return payload;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_PROJECTS_FAILURE,
          alertTitle: `Fetching of ${getProjectLabel(false, false)} List Failed`,
          showAlert: !throwError,
          throwError,
        }),
      );
  };
}

/// Create Project
function initiateCreateProjectRequest(payload: ICreateProjectParams) {
  return { type: CREATE_PROJECT_REQUEST, payload };
}

function createProjectRequestSuccess(payload: Project) {
  return { type: CREATE_PROJECT_SUCCESS, payload };
}

function initiateModifyProjectRequest(payload: InputModifyProjectInput) {
  return { type: MODIFY_PROJECT_REQUEST, payload };
}

function modifyProjectRequestSuccess(payload: Project) {
  return { type: MODIFY_PROJECT_SUCCESS, payload: payload };
}

export function createNewProject(payload: ICreateProjectParams): ReduxAction<Promise<Project>> {
  return (dispatch, _getState, webApiClient) => {
    dispatch(initiateCreateProjectRequest(payload));

    return webApiClient
      .saveProject(payload)
      .then((project: Project) => {
        const message = buildCreateProjectMessage(payload, project);

        dispatch(createProjectRequestSuccess(project));
        notify.info(
          {
            title: selMessage({
              id: "projects.create.successTitle",
              values: { projectLabel: getProjectLabel(true) },
            }),
            children: message,
          },
          { autoClose: 10000 },
        );
        dispatch(loadProjectList());
        return project;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: CREATE_PROJECT_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

export function modifyProject(payload: InputModifyProjectInput): ReduxAction<Promise<Project>> {
  return (dispatch, _getState, webApiClient) => {
    dispatch(initiateModifyProjectRequest(payload));

    return webApiClient
      .modifyProject(payload)
      .then((project: Project) => {
        dispatch(modifyProjectRequestSuccess(project));
        notify.info(
          {
            title: selMessage({
              id: "projects.modify.successTitle",
              values: { projectLabel: getProjectLabel(true) },
            }),
            children: buildModifyProjectMessage(project),
          },
          { autoClose: 10000 },
        );

        dispatch(loadProjectList()).then(() => {
          dispatch(loadProject(project.id));
        });
        return project;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: CREATE_PROJECT_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

export function validateProjectNameIsDefined(projectName: string, ignoreId: number): ReduxAction<Promise<boolean>> {
  return (_dispatch, _getState, webApiClient) => {
    if (!projectName) {
      // can happen because the async validation does take the previous value, and therefore this actual value might be empty
      return Promise.resolve(false);
    }
    return webApiClient.isProjectNameDefined(projectName, ignoreId);
  };
}

/*********** Utils ************* */

export function getCreateNewProjectButtonProps(user: User, action: () => void) {
  const canCreateProject = user && checkCapability(user.capabilities, CAPABILITY.CREATE_PROJECT);
  const tooltip = canCreateProject
    ? undefined
    : `You do not have permission to create new ${getProjectLabel(false, true)}`;
  return {
    disabled: !canCreateProject,
    title: tooltip,
    children: `Create a New ${getProjectLabel(true, false)}`,
    onPress: action,
  };
}
